import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Alert,
  Typography,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

import CpdActionButton from '@/components/ui/CpdActionButton';
import CpdAutocomplete from '@/components/ui/CpdAutocomplete';
import PermissionsPreviewTable from '@/components/PermissionsPreviewTable/PermissionsPreviewTable';
import { FacilitiesApi } from '@/api/clients/facilities/facilities.api';
import { UsersApi } from '@/api/clients/users/users.api';

const MirrorPermissionsDialog = ({
  open,
  onClose,
  targetUser,
  permissionType,
  organizationId,
  onApplyToForm,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState(null);

  const [sourceFacilityId, setSourceFacilityId] = useState('');
  const [sourceUserId, setSourceUserId] = useState(null);

  const [facilities, setFacilities] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingFacilities, setLoadingFacilities] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [dialogError, setDialogError] = useState(null);

  const isFacilityPermissions = permissionType === 'facility_permissions';

  useEffect(() => {
    if (open && isFacilityPermissions) {
      loadFacilities();
    }
  }, [open, isFacilityPermissions, organizationId]);

  useEffect(() => {
    if (open) {
      if (isFacilityPermissions && sourceFacilityId) {
        loadUsers(sourceFacilityId);
      } else if (!isFacilityPermissions && organizationId) {
        loadUsers();
      }
    }
  }, [open, sourceFacilityId, organizationId, isFacilityPermissions]);

  useEffect(() => {
    if (sourceUserId && open) {
      loadSourcePermissions();
    }
  }, [sourceUserId, open]);

  const loadFacilities = async () => {
    try {
      setLoadingFacilities(true);
      setDialogError(null);

      const facilities = await FacilitiesApi.getFacilitiesForForms({
        organizationId,
      });

      setFacilities(facilities || []);
    } catch (err) {
      console.error('Error loading facilities:', err);
      setDialogError(t('mirrorPermissions.errorLoadingFacilities'));
    } finally {
      setLoadingFacilities(false);
    }
  };

  const loadUsers = async (facilityId) => {
    try {
      setLoadingUsers(true);
      setDialogError(null);
      setSourceUserId(null);

      const params = {
        organization_id: organizationId,
        status: 'ACTIVE',
        limit: 100,
      };

      if (facilityId) {
        params.facility_id = facilityId;
      }

      const response = await UsersApi.getUsers(params);

      const userData = response.data.data;
      const filteredUsers = userData.filter(
        (user) => user.id !== targetUser?.id,
      );
      setUsers(filteredUsers);
    } catch (err) {
      console.error('Error loading users:', err);
      setDialogError(t('mirrorPermissions.errorLoadingUsers'));
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadSourcePermissions = async () => {
    try {
      setLoading(true);
      setDialogError(null);

      const params = {
        permission_type: permissionType,
      };

      if (isFacilityPermissions) {
        params.facility_id = sourceFacilityId;
      } else {
        params.organization_id = organizationId;
      }

      const response = await UsersApi.getUserPermissions(sourceUserId, params);
      setPermissions(response.data?.data || null);
    } catch (err) {
      if (err.response?.status === 404) {
        setDialogError(t('mirrorPermissions.noPermissions'));
      } else {
        setDialogError(t('mirrorPermissions.errorLoadingPermissions'));
      }
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyToForm = () => {
    if (!sourceUserId || !permissions) {
      setDialogError(t('mirrorPermissions.selectUserFirst'));
      return;
    }

    const sourceUser = users.find((u) => u.id === sourceUserId);

    let permissionsToApply;
    if (isFacilityPermissions) {
      permissionsToApply = permissions.facility_permissions;
    } else {
      permissionsToApply = permissions.organization_permissions;
    }

    if (onApplyToForm) {
      onApplyToForm(permissionsToApply, sourceUser);
    }

    handleClose();
  };

  const handleClose = () => {
    setSourceFacilityId('');
    setSourceUserId(null);
    setUsers([]);
    setFacilities([]);
    setPermissions(null);
    setDialogError(null);
    onClose();
  };

  const selectedSourceUser = users.find((u) => u.id === sourceUserId);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ContentCopyIcon color='primary' />
          <Typography variant='h6'>{t('mirrorPermissions.title')}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity='info' sx={{ mb: 3 }}>
          <Typography variant='body2'>
            {isFacilityPermissions
              ? t('mirrorPermissions.facilityContext', {
                  targetUser: targetUser?.name || targetUser?.email,
                })
              : t('mirrorPermissions.organizationContext', {
                  targetUser: targetUser?.name || targetUser?.email,
                })}
          </Typography>
        </Alert>

        {dialogError && (
          <Alert
            severity='error'
            sx={{ mb: 2 }}
            onClose={() => setDialogError(null)}>
            {dialogError}
          </Alert>
        )}

        {isFacilityPermissions && (
          <Box sx={{ mb: 3 }}>
            <CpdAutocomplete
              options={facilities}
              value={facilities.find((f) => f.id === sourceFacilityId) || null}
              onChange={(event, newValue) => {
                setSourceFacilityId(newValue?.id || '');
              }}
              getOptionLabel={(option) => option.name}
              label={t('mirrorPermissions.selectSourceFacility')}
              loading={loadingFacilities}
              disabled={loadingFacilities}
              fullWidth
            />
          </Box>
        )}

        {(!isFacilityPermissions || sourceFacilityId) && (
          <Box sx={{ mb: 3 }}>
            <CpdAutocomplete
              options={users}
              value={selectedSourceUser || null}
              onChange={(event, newValue) => {
                setSourceUserId(newValue?.id || null);
              }}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              label={t('mirrorPermissions.selectSourceUser')}
              loading={loadingUsers}
              disabled={
                loadingUsers || (isFacilityPermissions && !sourceFacilityId)
              }
              fullWidth
            />
          </Box>
        )}

        {loading && sourceUserId && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && permissions && sourceUserId && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography
              variant='subtitle1'
              gutterBottom
              sx={{ fontWeight: 'medium' }}>
              {t('mirrorPermissions.preview')}
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              {t('mirrorPermissions.previewDescription', {
                targetUserName: targetUser?.name || targetUser?.email,
              })}
            </Typography>
            <PermissionsPreviewTable
              facilityPermissions={
                isFacilityPermissions ? permissions.facility_permissions : null
              }
              organizationPermissions={
                !isFacilityPermissions
                  ? permissions.organization_permissions
                  : null
              }
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          startIcon={<CloseIcon />}
          disabled={loading}>
          {t('common.buttons.cancel')}
        </Button>
        <CpdActionButton
          onClick={handleApplyToForm}
          loading={loading}
          disabled={!sourceUserId || !permissions || loading}
          startIcon={<ContentCopyIcon />}>
          {t('mirrorPermissions.applyToForm')}
        </CpdActionButton>
      </DialogActions>
    </Dialog>
  );
};

export default MirrorPermissionsDialog;
